import Link from "next/link";
import Image from "next/image";
import {
  Facebook,
  Instagram,
  Linkedin,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
} from "lucide-react";
import Button from "../ui/Button";
import { useState, useEffect } from "react";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [footerData, setFooterData] = useState(null);

  useEffect(() => {
    async function getData() {
      const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL;
      try {
        const formData = new FormData();
        formData.append("action", "getHeaderData");

        const response = await fetch(`${BACKEND_URL}/manageData`, {
          method: "POST",
          body: formData,
        });
        const data = await response.json();
        setFooterData(data);
      } catch (error) {
        console.log("error", error);
      }
    }
    getData();
  }, []);

  const aboutData = [
    "SCC Infotech provides innovative technology solutions to businesses worldwide, helping them transform their digital presence and operational efficiency.",
    "Empowering organizations with scalable and secure digital solutions that enhance productivity, reduce operational costs, and support long-term business growth.",
    "We specialize in delivering future-ready technologies that streamline workflows, modernize infrastructure, and elevate user experiences across platforms.",
    "SCC Infotech is a trusted partner for digital transformation, offering tailored solutions that align with your business goals and accelerate your innovation journey.",
    "From strategy to execution, we build intelligent systems that simplify complexity, drive efficiency, and unlock untapped business potential.",
    "Our mission is to help global enterprises adopt next-generation technologies that improve performance, agility, and customer satisfaction.",
    "We collaborate with clients to design and implement tech-driven solutions that automate processes, enhance decision-making, and future-proof operations.",
    "With deep industry expertise and cutting-edge tools, SCC Infotech enables businesses to scale effectively in a competitive digital landscape.",
    "We deliver comprehensive IT services that empower organizations to innovate faster, adapt to change, and maximize return on technology investments.",
    "Our solutions are crafted to not only solve current challenges but to evolve alongside your business, ensuring long-term digital resilience.",
    "At SCC Infotech, we blend creativity, technology, and strategy to build impactful digital experiences that foster growth, trust, and performance."
  ];

  const getDailyPhrase = () => {
    const index = Math.floor(Math.random() * aboutData.length);
    return aboutData[index];
  };

  return (
    <footer className="relative z-[99] md:pt-10 bg-gradient-to-b from-[#172C5E] via-[#1a0575] to-[#0f172a] text-white">
      <div className="absolute z-[99999999] top-[3px] left-0 w-full overflow-hidden transform -translate-y-full">
        <svg
          className="relative block w-full h-16"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V120H0Z"
            className="fill-[#172C5E]"
          ></path>
        </svg>
      </div>

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-[10%] w-8 h-8 rounded-full bg-[#172C5E]/10 animate-[float_8s_ease-in-out_infinite]"></div>
        <div className="absolute top-40 left-[25%] w-6 h-6 rounded-full bg-[#8339EA]/10 animate-[float_12s_ease-in-out_infinite_1s]"></div>
        <div className="absolute top-60 left-[45%] w-10 h-10 rounded-full bg-[#8997FB]/10 animate-[float_10s_ease-in-out_infinite_0.5s]"></div>
        <div className="absolute top-32 left-[65%] w-5 h-5 rounded-full bg-[#1D3883]/10 animate-[float_9s_ease-in-out_infinite_1.5s]"></div>
        <div className="absolute top-52 left-[85%] w-7 h-7 rounded-full bg-[#172C5E]/10 animate-[float_11s_ease-in-out_infinite_0.2s]"></div>

        <div className="absolute top-[30%] left-[15%] w-40 h-40 rounded-full bg-gradient-to-r from-[#172C5E]/5 to-[#8339EA]/5 animate-[pulse_15s_ease-in-out_infinite]"></div>
        <div className="absolute top-[60%] left-[75%] w-32 h-32 rounded-full bg-gradient-to-r from-[#8997FB]/5 to-[#1D3883]/5 animate-[pulse_12s_ease-in-out_infinite_2s]"></div>

        <div className="absolute top-[20%] right-[20%] w-24 h-24 rotate-45 bg-[#172C5E]/5 animate-[spin_30s_linear_infinite]"></div>
        <div className="absolute bottom-[15%] left-[30%] w-20 h-20 rotate-12 bg-[#8339EA]/5 animate-[spin_25s_linear_infinite_reverse]"></div>

        <div className="absolute top-[15%] left-[40%] w-1 h-1 rounded-full bg-white/30 animate-[twinkle_4s_ease-in-out_infinite]"></div>
        <div className="absolute top-[25%] left-[60%] w-1 h-1 rounded-full bg-white/30 animate-[twinkle_5s_ease-in-out_infinite_0.5s]"></div>
        <div className="absolute top-[45%] left-[20%] w-1 h-1 rounded-full bg-white/30 animate-[twinkle_3s_ease-in-out_infinite_1s]"></div>
        <div className="absolute top-[65%] left-[50%] w-1 h-1 rounded-full bg-white/30 animate-[twinkle_6s_ease-in-out_infinite_1.5s]"></div>
        <div className="absolute top-[75%] left-[70%] w-1 h-1 rounded-full bg-white/30 animate-[twinkle_4.5s_ease-in-out_infinite_2s]"></div>

        <div className="absolute top-[0] left-0 w-full h-px bg-gradient-to-r from-transparent via-[#8997FB]/10 to-transparent animate-[fadeInOut_8s_ease-in-out_infinite]"></div>
        <div className="absolute top-[55%] left-0 w-full h-px bg-gradient-to-r from-transparent via-[#172C5E]/10 to-transparent animate-[fadeInOut_10s_ease-in-out_infinite_1s]"></div>
        <div className="absolute top-[85%] left-0 w-full h-px bg-gradient-to-r from-transparent via-[#8339EA]/10 to-transparent animate-[fadeInOut_12s_ease-in-out_infinite_2s]"></div>

        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute top-0 left-[10%] w-px h-[200%] bg-gradient-to-b from-transparent via-[#172C5E]/10 to-transparent rotate-45 animate-[rise_20s_linear_infinite]"></div>
          <div className="absolute top-0 left-[30%] w-px h-[200%] bg-gradient-to-b from-transparent via-[#8339EA]/10 to-transparent rotate-45 animate-[rise_25s_linear_infinite_5s]"></div>
          <div className="absolute top-0 left-[50%] w-px h-[200%] bg-gradient-to-b from-transparent via-[#8997FB]/10 to-transparent rotate-45 animate-[rise_22s_linear_infinite_2s]"></div>
          <div className="absolute top-0 left-[70%] w-px h-[200%] bg-gradient-to-b from-transparent via-[#1D3883]/10 to-transparent rotate-45 animate-[rise_28s_linear_infinite_7s]"></div>
          <div className="absolute top-0 left-[90%] w-px h-[200%] bg-gradient-to-b from-transparent via-[#172C5E]/10 to-transparent rotate-45 animate-[rise_24s_linear_infinite_3s]"></div>
        </div>

        <div className="absolute inset-0 bg-gradient-to-b from-[#172C5E]/0 via-[#8339EA]/0 to-[#0f172a]/10 animate-[breathe_10s_ease-in-out_infinite]"></div>
      </div>

      <div className="container mx-auto px-6 py-16 pb-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="space-y-4 col-span-2 grid grid-cols-1 lg:grid-cols-3 lg:gap-4 transform transition-all duration-500 ">
            <div className=" col-span-1 max-w-sm lg:col-span-2 ">
              <Link
                href="/"
                className="relative w-[64px] h-[64px]"
                aria-label="Go To HomePage"
              >
                <Image
                  src="/images/logo-white.webp"
                  alt="SCC Infotech1"
                  fill
                  priority
                  loading="eager"
                  sizes="(max-width: 768px) 64px, 90px"
                  className="object-contain"
                />
              </Link>

              <p className="text-white  text-wrap mt-4 text-base">
                {getDailyPhrase()}
              </p>
              <div className="flex space-x-4 mt-6">
                <Link
                  href="https://www.facebook.com/sccinfotech/"
                  target="_blank"
                  className="text-white hover:text-[#e0e7ef] hover:scale-125 transition-all duration-300 transform-gpu"
                  aria-label="Go To Facebook Page"
                >
                  <Facebook
                    size={24}
                    className="text-[#8997FB] group-hover:text-white transition-colors"
                  />
                </Link>
                <Link
                  href="https://www.instagram.com/sccinfotechllp"
                  target="_blank"
                  className="text-white hover:text-[#e0e7ef] hover:scale-125 transition-all duration-300 transform-gpu"
                  aria-label="Go To Instagram Page"
                >
                  <Instagram
                    size={24}
                    className="text-[#8997FB] group-hover:text-white transition-colors"
                  />
                </Link>
                <Link
                  href="https://www.linkedin.com/company/scc-infotech-llp/?originalSubdomain=in"
                  target="_blank"
                  className="text-white hover:text-[#e0e7ef] hover:scale-125 transition-all duration-300 transform-gpu"
                  aria-label="Go To LinkedIn Page"
                >
                  <Linkedin
                    size={24}
                    className="text-[#8997FB] group-hover:text-white transition-colors"
                  />
                </Link>
              </div>
            </div>
            <div className="transform transition-all duration-500 ">
              <p className="text-lg cursor-default font-bold mb-4 relative inline-block group text-white">
                Quick Links
                <span className="absolute -bottom-1 left-0 w-1/2 h-0.5 bg-gradient-to-r from-white to-[#8997FB]"></span>
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#8997FB] to-white group-hover:w-full transition-all duration-700"></span>
              </p>
              <ul className="space-y-2">
                {[
                  { label: "Home", route: "/" },
                  { label: "About Us", route: "/about" },
                  { label: "Services", route: "/services" },
                  { label: "Portfolio", route: "/portfolio" },
                  { label: "Blogs", route: "/blogs?search=&category=All" },
                  { label: "Contact Us", route: "/contact" },
                  { label: "Sitemap", route: "/sitemap.xml" },
                  { label: "Privacy Policy", route: "/privacy-policy" },
                  { label: "Terms of Use", route: "/terms-of-use" },
                ].map(({ label, route }) => (
                  <li
                    key={label}
                    className="transform transition-all duration-300 hover:translate-x-2 group"
                  >
                    <Link
                      href={route}
                      aria-label={`Go to ${label.toLowerCase()} page`}
                      className="text-white hover:text-[#e0e7ef] transition-colors inline-flex items-center"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="transform col-span-2 md:col-span-1 transition-all duration-500 ">
            <p className="text-lg cursor-default font-bold mb-4 relative inline-block group text-white">
              Our Services
              <span className="absolute -bottom-1 left-0 w-1/2 h-0.5 bg-gradient-to-r from-white to-[#8997FB]"></span>
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#8997FB] to-white group-hover:w-full transition-all duration-700"></span>
            </p>
            <ul className="space-y-2">
              {footerData?.services?.slice(0, 7).map((service) => (
                <li
                  key={service?.id}
                  className="transform transition-all duration-300 hover:translate-x-2 group"
                >
                  <Link
                    href={`/services#${service?.name
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                    className="text-white hover:text-[#e0e7ef] transition-colors inline-flex items-center"
                    aria-label={`go to services page ${service?.name}`}
                  >
                    {service?.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="transform col-span-2 md:col-span-1 transition-all duration-500 ">
            <p className="text-lg cursor-default font-bold mb-4 relative inline-block group text-white">
              Contact Us
              <span className="absolute -bottom-1 left-0 w-1/2 h-0.5 bg-gradient-to-r from-white to-[#8997FB]"></span>
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#8997FB] to-white group-hover:w-full transition-all duration-700"></span>
            </p>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3 group hover:bg-[#172C5E]/5 p-1 rounded-md transition-all duration-300">
                <MapPin className="h-5 min-w-5 text-[#8997FB] group-hover:text-white group-hover:scale-110 transition-all duration-300" />
                <span className="text-white group-hover:text-[#e0e7ef] transition-colors duration-300">
                  349-350, Vikas Shoppers, B/H Filter <br /> House Bhagvan Nagar
                  Circle, near Sarthana Jakat Naka, Nana Varachha, Surat,
                  Gujarat 395006
                </span>
              </li>
              <li className="flex items-center space-x-3 group hover:bg-[#172C5E]/5 p-1 rounded-md transition-all duration-300">
                <Phone className="h-5 min-w-5 text-[#8997FB] group-hover:text-white group-hover:scale-110 transition-all duration-300" />
                <span className="text-white group-hover:text-[#e0e7ef] transition-colors duration-300">
                  +91 99743 61458
                </span>
              </li>
              <li className="flex items-center space-x-3 group hover:bg-[#172C5E]/5 p-1 rounded-md transition-all duration-300">
                <Mail className="h-5 min-w-5 text-[#8997FB] group-hover:text-white group-hover:scale-110 transition-all duration-300" />
                <span className="text-white group-hover:text-[#e0e7ef] transition-colors duration-300">
                  info@sccinfotech.com
                </span>
              </li>
            </ul>

            <div className="mt-6">
              <Button
                href="/contact-us"
                variant="primary"
                size="md"
                rightIcon={
                  <ArrowRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform duration-300"
                  />
                }
                className="group relative overflow-hidden"
              >
                Get in Touch
                <span className="absolute top-0 left-[10%] w-1 h-1 rounded-full bg-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-[moveUpRight_2s_ease-in-out_infinite]"></span>
                <span className="absolute top-[60%] left-[20%] w-1 h-1 rounded-full bg-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-[moveUpRight_2.5s_ease-in-out_infinite_0.3s]"></span>
                <span className="absolute top-[80%] left-[40%] w-1 h-1 rounded-full bg-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-[moveUpRight_1.8s_ease-in-out_infinite_0.5s]"></span>
                <span className="absolute top-[30%] right-[30%] w-1 h-1 rounded-full bg-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-[moveUpLeft_2.2s_ease-in-out_infinite_0.2s]"></span>
                <span className="absolute top-[70%] right-[20%] w-1 h-1 rounded-full bg-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-[moveUpLeft_1.9s_ease-in-out_infinite_0.6s]"></span>
              </Button>
            </div>
          </div>
        </div>

        <div className="relative mt-12 pt-8 px-6 flex flex-col md:flex-row justify-between items-center">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#8997FB]/30 to-transparent animate-[pulse_4s_ease-in-out_infinite]"></div>
          <p className="text-white text-center w-full text-base">
            © {currentYear} SCC Infotech. All rights reserved.
          </p>
        </div>

        <div className="absolute top-0 left-0 w-16 h-16">
          <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-[#8339EA]/30 animate-[cornerPulse_4s_ease-in-out_infinite]"></div>
        </div>
        <div className="absolute top-0 right-0 w-16 h-16">
          <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-[#8339EA]/30 animate-[cornerPulse_4s_ease-in-out_infinite_0.5s]"></div>
        </div>
        <div className="absolute bottom-0 left-0 w-16 h-16">
          <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-[#8339EA]/30 animate-[cornerPulse_4s_ease-in-out_infinite_1s]"></div>
        </div>
        <div className="absolute bottom-0 right-0 w-16 h-16">
          <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-[#8339EA]/30 animate-[cornerPulse_4s_ease-in-out_infinite_1.5s]"></div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(180deg);
          }
        }
        @keyframes pulse {
          0%,
          100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
        @keyframes fadeInOut {
          0%,
          100% {
            opacity: 0.1;
          }
          50% {
            opacity: 0.5;
          }
        }
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes twinkle {
          0%,
          100% {
            opacity: 0.1;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.5);
          }
        }
        @keyframes rise {
          0% {
            transform: translateY(100%) rotate(45deg);
          }
          100% {
            transform: translateY(-100%) rotate(45deg);
          }
        }
        @keyframes breathe {
          0%,
          100% {
            opacity: 0.1;
          }
          50% {
            opacity: 0.3;
          }
        }
        @keyframes moveUpRight {
          0% {
            transform: translate(0, 0);
            opacity: 1;
          }
          100% {
            transform: translate(20px, -20px);
            opacity: 0;
          }
        }
        @keyframes moveUpLeft {
          0% {
            transform: translate(0, 0);
            opacity: 1;
          }
          100% {
            transform: translate(-20px, -20px);
            opacity: 0;
          }
        }
        @keyframes cornerPulse {
          0%,
          100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }
      `}</style>
    </footer>
  );
}
